import {
  resolveStorefrontFeatures,
  type SocialLinks,
  type StorefrontFeatureKey,
  type TenantRole,
} from "@loot-os/api-contract";
import type { LinkProps } from "@tanstack/react-router";
import {
  BarChart3,
  Code,
  FlaskConical,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Package,
  Settings,
  Store,
  Truck,
  Users,
  UsersRound,
} from "lucide-react";

/**
 * A static page under the tenant: the router's own union below `/tenants/$tenantId`, minus the
 * routes that take a second param (`boxes/$boxId`, `users/$userId`), so every path here links
 * with `params={{ tenantId }}` alone.
 */
export type TenantNavPath = Exclude<
  Extract<LinkProps["to"], `/tenants/$tenantId${string}`>,
  `/tenants/$tenantId/${string}$${string}`
>;

/** A hostname row as a sidebar status reads it: the three columns Domains' dot decides on. */
export interface StorefrontDomainSwitch {
  /** `subdomain` (provisioned under the root) or `custom` (tenant-attached). */
  type: string;
  /** `active` is the only status the resolver serves. */
  status: string;
  isPrimary: boolean;
}

/**
 * What a sidebar status reads from the `storefront.get` query every tenant page already runs,
 * for the active environment: the `config` row's switches, plus the environment's `domains`
 * beside it.
 */
export interface StorefrontSwitches {
  enabled: boolean;
  features: Partial<Record<StorefrontFeatureKey, boolean>>;
  intercomEnabled: boolean;
  /** Parsed; `{}` when none is set. */
  socialLinks: SocialLinks;
  /** Every hostname of the environment, the provisioned subdomain included. */
  domains: readonly StorefrontDomainSwitch[];
}

/** A hub's section page: folded under the hub in the sidebar, named after it in the breadcrumb. */
export interface TenantNavChild {
  to: TenantNavPath;
  label: string;
  /** Extra terms the palette finds the page by: the names of pages folded into this one. */
  keywords?: readonly string[];
  /**
   * For a page with one state worth a glance: whether the active environment has its switch on
   * (Storefront, Payments, Battles, Chat, Forge, Support chat), for Social links at least one network set,
   * or for Domains an active custom domain as its primary. The sidebar shows a green dot on
   * the row while it does and a muted one otherwise, so the state reads without opening the
   * page; the screen reader hears "{label} enabled" / "{label} disabled".
   */
  status?: (config: StorefrontSwitches) => boolean;
  /**
   * A section page that serves a higher role than its hub (the hub's own when unset): the
   * sidebar and the palette hide it below that, and a hub left with no section reads as a
   * plain page.
   */
  minRole?: TenantRole;
}

export interface TenantNavItem extends TenantNavChild {
  icon: LucideIcon;
  /** Highlight only on the exact path, for the overview, whose path prefixes every other. */
  exact: boolean;
  /**
   * The tenant role the page serves: the sidebar and the palette hide it below that, and the
   * server forbids it regardless.
   */
  minRole: TenantRole;
  /** The section pages of a hub, in the hub's own order. They inherit `minRole` unless they set one. */
  children?: readonly TenantNavChild[];
}

/** One section of the sidebar in tenant scope: a label (none for the top) and its pages. */
export interface TenantNavGroup {
  key: string;
  label: string | null;
  items: readonly TenantNavItem[];
}

/**
 * The pages inside a tenant, rendered with the current $tenantId param and grouped by what
 * they read: the top section is the active environment's pages (every `*EnvironmentProcedure`
 * reader), "Developer" the environment's integration pages, "Tenant" the pages that are the
 * same in every environment (`tenantProcedure` / `adminProcedure` readers). A mixed page sits
 * where its primary read lives: Settings (tenant settings, plus the platform-only
 * per-environment KYC switches) under Tenant; Payments (the Stripe account is the tenant's,
 * but the switch, the packs, the purchases and the disputes are the environment's) and Domains
 * (list and add per environment; retry, remove and setPrimary tenant-wide) as Storefront
 * sections, since what they configure is the environment's storefront.
 *
 * Kept apart from `PLATFORM_TOOLS` (`platformTools.ts`) rather than merged: these take a route
 * param, and one `to` union spanning both would make `<Link params>` unassignable. The sidebar,
 * the ⌘K palette and the breadcrumb trail all read this list, so a page added here needs no
 * entry anywhere else.
 */
const NAV = [
  {
    key: "store",
    label: null,
    items: [
      {
        to: "/tenants/$tenantId",
        label: "Overview",
        icon: LayoutDashboard,
        exact: true,
        minRole: "viewer",
      },
      {
        to: "/tenants/$tenantId/boxes",
        label: "Boxes",
        icon: Package,
        exact: false,
        minRole: "viewer",
      },
      {
        to: "/tenants/$tenantId/users",
        label: "Shoppers",
        icon: Users,
        exact: false,
        minRole: "viewer",
      },
      {
        to: "/tenants/$tenantId/analytics",
        label: "Analytics",
        icon: BarChart3,
        exact: false,
        minRole: "viewer",
        keywords: ["page views", "rollups"],
        children: [
          {
            // Viewer-level like the hub: it reads the rollups, never raw events.
            to: "/tenants/$tenantId/analytics/insights",
            label: "Insights",
            keywords: ["trends", "compare events", "chart", "series", "mixpanel"],
          },
          {
            // Admin because the page reads `analytics.events`, an adminEnvironmentProcedure.
            to: "/tenants/$tenantId/analytics/events",
            label: "Event log",
            minRole: "admin",
            keywords: ["events", "raw events", "line items", "timeline", "session"],
          },
        ],
      },
      {
        to: "/tenants/$tenantId/storefront",
        label: "Storefront",
        icon: Store,
        exact: false,
        minRole: "viewer",
        // The row itself is the Storefront page: the status switch, then a tab each for General
        // (branding), Theme and Terminology, hence the theme and wording terms here. The sections beneath it run
        // address → brand → store → policies; each page gates its own edits on `can("editor")`.
        keywords: [
          "branding",
          "logo",
          "favicon",
          "theme",
          "rarity",
          "colors",
          "palette",
          "terminology",
          "wording",
          "names",
          "labels",
        ],
        status: (config) => config.enabled,
        children: [
          {
            // Admin because the page reads `domains.list`, an adminEnvironmentProcedure. The dot
            // says whether the store answers on a domain of the tenant's own: a custom hostname
            // that is active and primary; the provisioned subdomain as primary reads as off.
            to: "/tenants/$tenantId/storefront/domains",
            label: "Domains",
            minRole: "admin",
            keywords: ["custom domain", "dns", "hostname", "certificate", "primary domain"],
            status: (config) =>
              config.domains.some(
                (domain) =>
                  domain.type === "custom" && domain.status === "active" && domain.isPrimary,
              ),
          },
          {
            // No dot: every environment renders a template, so there is no off state.
            to: "/tenants/$tenantId/storefront/template",
            label: "Template",
            keywords: ["layout", "home page", "storefront template", "classic"],
          },
          {
            to: "/tenants/$tenantId/storefront/social-links",
            label: "Social links",
            status: (config) => Object.values(config.socialLinks).some((url) => Boolean(url)),
          },
          {
            // Every member's page: the switch and the packs read at viewer; the Stripe account,
            // purchases, disputes and revenue show to admins alone.
            to: "/tenants/$tenantId/storefront/payments",
            label: "Payments",
            keywords: [
              "currency",
              "stripe",
              "top-up packs",
              "packs",
              "purchases",
              "disputes",
              "revenue",
            ],
            status: (config) => resolveStorefrontFeatures(config.features).balancePurchases,
          },
          {
            to: "/tenants/$tenantId/storefront/battles",
            label: "Battles",
            keywords: ["loser prize"],
            status: (config) => resolveStorefrontFeatures(config.features).battles,
          },
          {
            // The switch, then moderation: the room, reports and mutes.
            to: "/tenants/$tenantId/storefront/chat",
            label: "Chat",
            keywords: ["chat room", "shopper chat", "moderation", "mute", "reports", "messages"],
            status: (config) => resolveStorefrontFeatures(config.features).chat,
          },
          {
            // The switch, the creator commission and the preset images shoppers build boxes with.
            to: "/tenants/$tenantId/storefront/forge",
            label: "Forge",
            keywords: ["community boxes", "forge boxes", "creator cut", "commission", "box images"],
            status: (config) => resolveStorefrontFeatures(config.features).forge,
          },
          {
            to: "/tenants/$tenantId/storefront/support",
            label: "Support chat",
            keywords: ["intercom"],
            status: (config) => config.intercomEnabled,
          },
          {
            to: "/tenants/$tenantId/storefront/legal",
            label: "Legal / Consent",
            keywords: ["terms", "privacy", "termly", "cookie consent", "banner"],
          },
        ],
      },
      {
        to: "/tenants/$tenantId/shipping",
        label: "Shipping",
        icon: Truck,
        exact: false,
        minRole: "viewer",
        keywords: ["destinations", "countries", "delivery"],
      },
    ],
  },
  {
    key: "developer",
    label: "Developer",
    items: [
      {
        // The environment's internal storefront key is shown here too, above the table.
        to: "/tenants/$tenantId/keys",
        label: "API keys",
        icon: KeyRound,
        exact: false,
        minRole: "admin",
        keywords: ["secret key", "publishable key", "storefront key"],
      },
      {
        // Admin because the page reads `apiKeys.list`, an adminEnvironmentProcedure.
        to: "/tenants/$tenantId/embed",
        label: "Embed",
        icon: Code,
        exact: false,
        minRole: "admin",
      },
    ],
  },
  {
    key: "tenant",
    label: "Tenant",
    items: [
      {
        to: "/tenants/$tenantId/sandboxes",
        label: "Sandboxes",
        icon: FlaskConical,
        exact: false,
        minRole: "admin",
      },
      {
        to: "/tenants/$tenantId/team",
        label: "Team",
        icon: UsersRound,
        exact: false,
        minRole: "admin",
      },
      {
        to: "/tenants/$tenantId/settings",
        label: "Settings",
        icon: Settings,
        exact: false,
        minRole: "admin",
      },
    ],
  },
] as const satisfies readonly TenantNavGroup[];

export const TENANT_NAV: readonly TenantNavGroup[] = NAV;

type NavItem = (typeof NAV)[number]["items"][number];
type ChildOf<Item> = Item extends { children: readonly (infer Child)[] } ? Child : never;
/** Every path the registry names, as literals, for the breadcrumb drift guard. */
export type TenantNavCoveredPath = NavItem["to"] | ChildOf<NavItem>["to"];
